import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  basketExpiresAt,
  emptyBasket,
  isBasketEmpty,
  nextFilledAt,
  PersistedBasket,
  readStoredBasket,
  writeStoredBasket,
} from './basketStorage';
import { isManualBookingPath } from './manualBookingFlow';
import { Basket } from './search/index/type';

type BasketContextValue = {
  /** The current basket (flights + accommodations). */
  basket: Basket;
  /** Update the basket; the new value is persisted so it survives navigation and reloads. */
  setBasket: Dispatch<SetStateAction<Basket>>;
  /** Empty the basket — used after a successful booking and on logout. */
  clearBasket: () => void;
  /** Epoch ms at which the basket self-clears, or null while it is empty. Lets checkout warn before it goes. */
  expiresAt: number | null;
  /**
   * True only when the basket was emptied by the TTL rather than by the user or a completed booking. Checkout reads
   * this to redirect to search, which it can't infer from an empty basket alone: a successful order empties it too.
   */
  expired: boolean;
  /** True once checkout has been reached for this basket. The header shortcut stays hidden until then. */
  checkoutSeen: boolean;
  /** True while checkout is mounted, so the header shortcut stands down rather than duplicating what is on screen. */
  checkoutOnScreen: boolean;
  /** Registered by checkout on mount/unmount. */
  setCheckoutOnScreen: (onScreen: boolean) => void;
  /** Called by checkout on mount to unlock the header shortcut for the rest of this basket's life. */
  markCheckoutSeen: () => void;
};

export const BasketContext = createContext<BasketContextValue | null>(null);

/**
 * Basket store for the manual-booking flow. Held above the authenticated routes (not just the manual-booking pages)
 * so the header cart badge, which renders in the shared app header, can read it, and mirrored to localStorage so it
 * survives a reload mid-booking. Its life is still the flow's: navigating out of manual booking empties it.
 *
 * The basket also self-clears once its items are older than the TTL: shopped offers go stale server-side, and a basket
 * left open for hours was showing fares that could no longer be booked at the price displayed.
 */
export const BasketProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [persisted, setPersisted] = useState<PersistedBasket>(() => readStoredBasket(Date.now()));
  const [expired, setExpired] = useState(false);
  const { basket, filledAt, checkoutSeen } = persisted;
  // Transient, not persisted: whether checkout is on screen right now, which only this session can know.
  const [checkoutOnScreen, setCheckoutOnScreen] = useState(false);

  const setBasket = useCallback<Dispatch<SetStateAction<Basket>>>((action) => {
    // Any deliberate basket change starts a fresh session, so a previous timeout is no longer what the user is seeing.
    setExpired(false);
    setPersisted((previous) => {
      const next = typeof action === 'function' ? action(previous.basket) : action;
      return {
        basket: next,
        filledAt: nextFilledAt(previous.filledAt, next, Date.now()),
        // Emptying the basket ends the trip it belonged to, so the next one has to earn the shortcut again.
        checkoutSeen: isBasketEmpty(next) ? false : previous.checkoutSeen,
      };
    });
  }, []);

  const clearBasket = useCallback(() => {
    setExpired(false);
    setPersisted({ basket: emptyBasket, filledAt: null, checkoutSeen: false });
  }, []);

  const markCheckoutSeen = useCallback(() => {
    setPersisted((previous) => (previous.checkoutSeen ? previous : { ...previous, checkoutSeen: true }));
  }, []);

  const { pathname } = useLocation();
  const inManualBookingFlow = isManualBookingPath(pathname);

  // The basket belongs to the manual-booking flow, not to the app. The provider sits above every route only so the
  // header shortcut can read it, so leaving the flow has to end the basket explicitly: without this, a cart built in
  // search followed the user into Trips, Reporting and the agent and sat there until its TTL ran out.
  useEffect(() => {
    if (inManualBookingFlow) {
      return;
    }

    setExpired(false);
    setPersisted((previous) =>
      isBasketEmpty(previous.basket) && !previous.checkoutSeen
        ? previous
        : { basket: emptyBasket, filledAt: null, checkoutSeen: false },
    );
  }, [inManualBookingFlow]);

  useEffect(() => {
    writeStoredBasket(basket, filledAt, checkoutSeen);
  }, [basket, filledAt, checkoutSeen]);

  const expiresAt = basketExpiresAt(filledAt);

  // Expiry has to fire while the app is open, not just on the next load — the reported baskets were sitting in a tab
  // that was never reloaded. Background tabs have their timers throttled, so the deadline is re-checked whenever the
  // tab is brought back to the foreground rather than trusting the timer to have fired on time.
  useEffect(() => {
    if (expiresAt === null) {
      return;
    }

    const clearIfExpired = () => {
      if (Date.now() >= expiresAt) {
        setPersisted({ basket: emptyBasket, filledAt: null, checkoutSeen: false });
        setExpired(true);
      }
    };

    const timer = setTimeout(clearIfExpired, Math.max(0, expiresAt - Date.now()));
    document.addEventListener('visibilitychange', clearIfExpired);
    window.addEventListener('focus', clearIfExpired);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', clearIfExpired);
      window.removeEventListener('focus', clearIfExpired);
    };
  }, [expiresAt]);

  const value = useMemo(
    () => ({
      basket,
      setBasket,
      clearBasket,
      expiresAt,
      expired,
      checkoutSeen,
      checkoutOnScreen,
      setCheckoutOnScreen,
      markCheckoutSeen,
    }),
    [basket, setBasket, clearBasket, expiresAt, expired, checkoutSeen, checkoutOnScreen, markCheckoutSeen],
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
};
